import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/portal/consumption`;
const eur = (c) => `${((c || 0) / 100).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const box = 'bg-[#0B0D10] border border-[#1F242C] p-6';
const th = 'py-2 pr-3 font-normal';

function Kpi({ label, value, id }) {
  return <div className="border border-[#1F242C] p-4" data-testid={`consumption-kpi-${id}`}><p className="text-[11px] uppercase tracking-wider text-[#5A5F67]">{label}</p><p className="text-xl font-mono text-white mt-1">{value}</p></div>;
}

export default function AdminConsumptionPanel({ headers }) {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(null);
  const load = () => axios.get(API, { headers }).then((r) => setD(r.data)).catch(() => toast.error('Consumi non caricati'));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return <Loader2 className="animate-spin text-[#D4AF37]" />;
  const max = Math.max(1, ...d.services.map((s) => s.cents));
  return (
    <div className="space-y-6" data-testid="admin-consumption-panel">
      <div className={box}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-heading text-sm uppercase tracking-widest text-[#BFBFBF]">Consumi del mese · {d.label}</h3>
          <button onClick={load} className="h-8 px-3 text-xs flex items-center gap-1 border border-[#1F242C] text-[#BFBFBF] hover:text-white rounded-sm" data-testid="consumption-refresh"><RefreshCw size={12} />Aggiorna</button>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <Kpi id="gross" label="Consumi lordi" value={eur(d.total_cents)} /><Kpi id="refunds" label="Rimborsi" value={eur(d.refunds_cents)} />
          <Kpi id="net" label="Consumi netti" value={eur(d.net_cents)} /><Kpi id="count" label="Operazioni" value={d.count} />
        </div>
        <p className="text-[11px] uppercase tracking-wider text-[#5A5F67] mb-2">Per servizio</p>
        {d.services.length === 0 ? <p className="text-sm text-[#5A5F67]" data-testid="consumption-empty">Nessun consumo questo mese.</p> : (
          <table className="w-full text-sm" data-testid="consumption-services"><tbody>{d.services.map((s) => (
            <tr key={s.service} className="border-b border-[#1F242C]" data-testid={`consumption-service-${s.service}`}>
              <td className="py-2 pr-3 text-white w-56">{s.service}</td>
              <td className="py-2 pr-3"><div className="h-1.5 bg-[#1F242C] rounded-full"><div className="h-full bg-[#D4AF37] rounded-full" style={{ width: `${(s.cents / max) * 100}%` }} /></div></td>
              <td className="py-2 pr-3 text-xs text-[#7A7F87] w-40">{s.count} operazioni · {s.clients} clienti</td>
              <td className="py-2 font-mono text-right text-[#BFBFBF] w-28">{eur(s.cents)}</td>
            </tr>
          ))}</tbody></table>
        )}
      </div>
      <div className={box}>
        <p className="text-[11px] uppercase tracking-wider text-[#5A5F67] mb-2">Per cliente</p>
        <div className="overflow-x-auto"><table className="w-full text-sm" data-testid="consumption-clients">
          <thead><tr className="text-left text-[11px] uppercase tracking-wider text-[#5A5F67] border-b border-[#1F242C]"><th className={th}>Cliente</th><th className={th}>Operazioni</th><th className={`${th} text-right`}>Lordo</th><th className={`${th} text-right`}>Rimborsi</th><th className={`${th} text-right`}>Netto</th></tr></thead>
          <tbody>{d.clients.map((c) => (
            <React.Fragment key={c.id}>
              <tr className="border-b border-[#1F242C] cursor-pointer hover:bg-white/[0.02]" onClick={() => setOpen(open === c.id ? null : c.id)} data-testid={`consumption-client-${c.id}`}>
                <td className="py-2 pr-3"><p className="text-white">{c.company || c.email}</p><p className="text-xs text-[#5A5F67]">{c.email}</p></td>
                <td className="py-2 pr-3 text-[#BFBFBF]">{c.count}</td>
                <td className="py-2 pr-3 font-mono text-right text-[#BFBFBF]">{eur(c.cents)}</td>
                <td className="py-2 pr-3 font-mono text-right text-[#7A7F87]">{c.refunds_cents ? `−${eur(c.refunds_cents)}` : '—'}</td>
                <td className="py-2 font-mono text-right text-[#D4AF37]" data-testid={`consumption-client-net-${c.id}`}>{eur(c.net_cents)}</td>
              </tr>
              {open === c.id && <tr data-testid={`consumption-client-detail-${c.id}`}><td colSpan={5} className="py-2 pl-4 text-xs text-[#8A8F97]">{Object.entries(c.services).sort((a, b) => b[1] - a[1]).map(([k, v]) => `${k}: ${eur(v)}`).join(' · ') || 'Solo rimborsi'}</td></tr>}
            </React.Fragment>
          ))}</tbody>
        </table></div>
      </div>
    </div>
  );
}
